import { createNoise3D, NoiseFunction3D } from 'simplex-noise';

/**
 * VectorField handles the procedural generation of the movement forces.
 * It uses a 3D Simplex noise field (X, Y, and Time) to create an evolving flow pattern.
 */
export class VectorField {
  private noise3D: NoiseFunction3D;
  private time: number = 0;
  private noiseScale: number = 1.8;
  private evolutionSpeed: number = 0.15;
  private mouseInfluenceRadius: number = 0.35;
  private mouseStrength: number = 1.5;

  constructor() {
    // Initialize the noise function
    this.noise3D = createNoise3D();
  }

  /**
   * Updates the temporal dimension of the field.
   * @param deltaTime - Time passed since last frame in seconds.
   */
  update(deltaTime: number): void {
    this.time += deltaTime * this.evolutionSpeed;
  }

  /**
   * Calculates the flow vector at a given position [-1, 1].
   * Incorporates local mouse interaction logic.
   * 
   * @param x - Normalized x coordinate [-1, 1]
   * @param y - Normalized y coordinate [-1, 1]
   * @param mousePos - Normalized mouse coordinates [-1, 1]
   * @returns A 2D vector [vx, vy] representing the force direction.
   */
  getForce(x: number, y: number, mousePos: { x: number; y: number }): [number, number] {
    // Base noise field value
    // We use two noise samples to get independent X and Y flow components
    // or we can use the angle approach for more coherent flow.
    const angle = this.noise3D(x * this.noiseScale, y * this.noiseScale, this.time) * Math.PI * 2.5;
    
    let vx = Math.cos(angle);
    let vy = Math.sin(angle);

    // Local Glimmer / Mouse Interaction
    // Particles are gently pushed away or swirled around the mouse position
    const dx = x - mousePos.x;
    const dy = y - mousePos.y;
    const distSq = dx * dx + dy * dy;
    const radiusSq = this.mouseInfluenceRadius * this.mouseInfluenceRadius;

    if (distSq < radiusSq) {
      const strength = (1.0 - Math.sqrt(distSq) / this.mouseInfluenceRadius) * this.mouseStrength;
      
      // Create a swirling/repulsion effect
      // Adding a perpendicular component to the repulsion for 'turbulence'
      vx += (dx / Math.sqrt(distSq) + dy * 0.5) * strength;
      vy += (dy / Math.sqrt(distSq) - dx * 0.5) * strength;
    }

    // Normalizing the resulting force to keep movement consistent
    const mag = Math.sqrt(vx * vx + vy * vy);
    return [vx / mag, vy / mag];
  }

  /**
   * Allows adjusting the field complexity dynamically.
   */
  setComplexity(scale: number): void {
    this.noiseScale = scale;
  }

  /**
   * Allows adjusting how fast the 'thoughts' evolve.
   */
  setEvolutionSpeed(speed: number): void {
    this.evolutionSpeed = speed;
  }

  /**
   * Resets the noise field to a new random state.
   */
  regenerate(): void {
    this.noise3D = createNoise3D();
  }
}